import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  UserCheck,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  Video,
  MapPin,
  Building2,
  User,
  Phone,
  Mail,
  AlertCircle
} from 'lucide-react'
import { useVendorDashboard } from '../VendorDashboard'

const VendorInterviews = () => {
  const { isLoading } = useVendorDashboard()

  // Mock data - replace with actual API data
  const [interviews] = useState([
    {
      id: 'INT-2024-001',
      poId: 'PO-2024-001',
      companyName: 'Tech Corp Inc.',
      resourceName: 'John Doe',
      resourceId: 1,
      position: 'Senior React Developer',
      status: 'scheduled',
      scheduledDate: '2024-01-22',
      scheduledTime: '10:00 AM',
      interviewType: 'video',
      interviewLink: 'https://meet.google.com/abc-defg-hij',
      interviewerName: 'Jane Smith',
      interviewerEmail: 'jane.smith@techcorp.com',
      interviewerPhone: '+91 9876543210',
      location: null,
      notes: 'Technical interview focusing on React and TypeScript'
    },
    {
      id: 'INT-2024-002',
      poId: 'PO-2024-002',
      companyName: 'Digital Solutions Ltd.',
      resourceName: 'Sarah Johnson',
      resourceId: 2,
      position: 'Full Stack Developer',
      status: 'completed',
      scheduledDate: '2024-01-18',
      scheduledTime: '2:00 PM',
      interviewType: 'in-person',
      interviewLink: null,
      interviewerName: 'Mike Brown',
      interviewerEmail: 'mike.brown@digitalsolutions.com',
      interviewerPhone: '+91 9876543211',
      location: '123 Business Park, Mumbai',
      notes: 'Interview completed successfully. Awaiting feedback.'
    },
    {
      id: 'INT-2024-003',
      poId: 'PO-2024-003',
      companyName: 'Innovation Labs',
      resourceName: 'David Wilson',
      resourceId: 3,
      position: 'DevOps Engineer',
      status: 'scheduled',
      scheduledDate: '2024-01-25',
      scheduledTime: '11:00 AM',
      interviewType: 'video',
      interviewLink: 'https://zoom.us/j/123456789',
      interviewerName: 'Lisa Anderson',
      interviewerEmail: 'lisa.anderson@innovationlabs.com',
      interviewerPhone: '+91 9876543212',
      location: null,
      notes: 'DevOps technical assessment'
    }
  ])

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: {
        bg: 'bg-warning-100 dark:bg-warning-900/30',
        text: 'text-warning-700 dark:text-warning-300',
        icon: <Clock className="w-4 h-4" />,
        label: 'Scheduled'
      },
      completed: {
        bg: 'bg-success-100 dark:bg-success-900/30',
        text: 'text-success-700 dark:text-success-300',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Completed'
      },
      cancelled: {
        bg: 'bg-error-100 dark:bg-error-900/30',
        text: 'text-error-700 dark:text-error-300',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Cancelled'
      }
    }

    const config = statusConfig[status] || statusConfig.scheduled
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    )
  }

  const scheduledInterviews = interviews.filter(i => i.status === 'scheduled')
  const completedInterviews = interviews.filter(i => i.status === 'completed')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          <p className="text-secondary-600 dark:text-secondary-400">Loading interviews...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
            Interviews
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            Manage interviews scheduled with companies for your resources
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 shadow-glow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Scheduled</p>
              <p className="text-2xl font-bold text-warning-600">{scheduledInterviews.length}</p>
            </div>
            <Clock className="w-8 h-8 text-warning-600 dark:text-warning-400" />
          </div>
        </div>
        <div className="card p-4 shadow-glow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Completed</p>
              <p className="text-2xl font-bold text-success-600">{completedInterviews.length}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-success-600 dark:text-success-400" />
          </div>
        </div>
        <div className="card p-4 shadow-glow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Total</p>
              <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{interviews.length}</p>
            </div>
            <UserCheck className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="card p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-1">
              Interview Process
            </h3>
            <p className="text-xs text-primary-700 dark:text-primary-300">
              After accepting a PO, interviews are scheduled between your resources and the company. 
              You can view interview details, reschedule if needed, and track completion status.
            </p>
          </div>
        </div>
      </div>

      {/* Scheduled Interviews */}
      {scheduledInterviews.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
            Scheduled Interviews
          </h2>
          <div className="space-y-4">
            {scheduledInterviews.map((interview, index) => (
              <motion.div
                key={interview.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 shadow-glow hover:shadow-xl transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                            {interview.id}
                          </h3>
                          {getStatusBadge(interview.status)}
                        </div>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400">
                          PO: {interview.poId} • {interview.companyName}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Resource</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                            {interview.resourceName}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Building2 className="w-5 h-5 text-accent-600 dark:text-accent-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Position</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                            {interview.position}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Date & Time</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                            {new Date(interview.scheduledDate).toLocaleDateString()} at {interview.scheduledTime}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        {interview.interviewType === 'video' ? (
                          <Video className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <MapPin className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Type</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 capitalize">
                            {interview.interviewType === 'video' ? 'Video Call' : 'In-Person'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4 p-3 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">Interviewer Details</p>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                          {interview.interviewerName}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-secondary-600 dark:text-secondary-400">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {interview.interviewerEmail}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {interview.interviewerPhone}
                          </span>
                        </div>
                        {interview.interviewType === 'video' && interview.interviewLink && (
                          <a
                            href={interview.interviewLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                          >
                            <Video className="w-3 h-3" />
                            Join Interview Link
                          </a>
                        )}
                        {interview.interviewType === 'in-person' && interview.location && (
                          <p className="text-xs text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {interview.location}
                          </p>
                        )}
                      </div>
                    </div>

                    {interview.notes && (
                      <div>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Notes</p>
                        <p className="text-sm text-secondary-700 dark:text-secondary-300">{interview.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 lg:min-w-[200px]">
                    <button className="btn-primary text-sm font-semibold py-2">
                      View Details
                    </button>
                    {interview.status === 'scheduled' && (
                      <>
                        <button className="btn-outline text-sm font-semibold py-2">
                          Reschedule
                        </button>
                        <button className="btn-outline text-sm font-semibold py-2 text-error-600 dark:text-error-400 border-error-200 dark:border-error-800 hover:bg-error-50 dark:hover:bg-error-900/20">
                          Cancel Interview
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Interviews */}
      {completedInterviews.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
            Completed Interviews
          </h2>
          <div className="space-y-4">
            {completedInterviews.map((interview, index) => (
              <motion.div
                key={interview.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 shadow-glow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                        {interview.id}
                      </h3>
                      {getStatusBadge(interview.status)}
                    </div>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                      {interview.resourceName} • {interview.position} • {interview.companyName}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400">
                      Completed on {new Date(interview.scheduledDate).toLocaleDateString()} at {interview.scheduledTime}
                    </p>
                  </div>
                  <button className="btn-outline text-sm font-semibold py-2">
                    View Feedback
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {interviews.length === 0 && (
        <div className="card p-12 text-center">
          <UserCheck className="w-16 h-16 text-secondary-300 dark:text-secondary-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-2">
            No Interviews Scheduled
          </h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            Interviews will appear here once purchase orders are accepted and interviews are scheduled.
          </p>
        </div>
      )}
    </div>
  )
}

export default VendorInterviews

